import React, { useState, useRef, useEffect, useCallback } from 'react';
import useSupercluster from "use-supercluster";
import GoogleMapReact from "google-map-react";
import InfoWindow from '../InfoWindow';
import StatePanel from '../StatePanel/StatePanel';
import './Map.css';

// Estilos melhorados para o tema escuro do mapa com maior contraste e nitidez
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1f2a37" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }, { saturation: 50 }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4a5568" }]
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#e9e9e9" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }]
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8fad15" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }]
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }]
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }]
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#70b5ff" }]
  }
];

// Estilos melhorados para o tema claro com melhor nitidez
const lightMapStyles = [
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c0d6eb" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#c3e6a3" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f5d480" }]
  }
];

const DEFAULT_CENTER = {
  lat: 20,
  lng: 0
};

const DEFAULT_ZOOM = 2;

const ClusterMarker = ({ onClick, label }) => (
  <div
    className="cluster map-cluster"
    style={{ width: 15, height: 15, zIndex: 9999 }}
    onClick={onClick}
  >
    {label}
  </div>
);

const Map = (props) => {
  const mapRef = useRef();

  const { points, mapPoints, darkMode, selectedState, setSelectedState } = props;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const [center, setCenter] = useState(DEFAULT_CENTER);

  const [mapStyles, setMapStyles] = useState(darkMode ? darkMapStyles : lightMapStyles);

  // Atualiza os estilos do mapa quando o tema muda
  useEffect(() => {
    setMapStyles(darkMode ? darkMapStyles : lightMapStyles);
  }, [darkMode]);

  const fitMapToPoints = useCallback(() => {
    if (!mapRef.current || !window.google?.maps || !points.length) return;

    if (points.length === 1) {
      const [lng, lat] = points[0].geometry.coordinates;
      setCenter({ lat, lng });
      setZoom(4);
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(4);
      return;
    }

    const latLngBounds = new window.google.maps.LatLngBounds();

    points.forEach((point) => {
      const [lng, lat] = point.geometry.coordinates;

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        latLngBounds.extend({ lat, lng });
      }
    });

    if (latLngBounds.isEmpty()) return;

    mapRef.current.fitBounds(latLngBounds, 80);
  }, [points]);

  useEffect(() => {
    fitMapToPoints();
  }, [fitMapToPoints]);

  const handleCloseInfoWindown = () => {
    setSelectedEquipment(null);
    setCenter(DEFAULT_CENTER);
    setZoom(DEFAULT_ZOOM);
    fitMapToPoints();
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 30, maxZoom: 15 }
  });

  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const handleMarkerClick = (markerData) => {
    setSelectedState(markerData);
  };

  const handleClosePanel = () => {
    setSelectedState(null);
  };

  const mapOptions = (isDark) => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControlOptions: {
      style: 2, // DROPDOWN_MENU
      position: 3 // RIGHT_TOP
    }
  });

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num;
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={DEFAULT_CENTER}
        center={center}
        zoom={zoom}
        defaultZoom={DEFAULT_ZOOM}
        options={mapOptions(darkMode)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
          fitMapToPoints();
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ]);
        }}
      >
        {clusters.map(marker => {
          const [longitude, latitude] = marker.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount
          } = marker.properties;

          const key = isCluster
            ? `cluster-${marker.id ?? marker.properties.cluster_id ?? `${longitude}-${latitude}-${pointCount}`}`
            : `point-${marker.properties?.data?.sigla ?? `${longitude}-${latitude}`}`;

          return (
            <ClusterMarker
              key={key}
              lat={latitude}
              lng={longitude}
              onClick={() => handleMarkerClick(marker?.properties?.data)}
              label={formatNumber(isCluster ? pointCount : marker?.properties?.data?.totalMeasurements)}
            />
          )
        })}

        {selectedEquipment &&
          <InfoWindow
            close={handleCloseInfoWindown}
            data={selectedEquipment}
            lat={selectedEquipment.lat}
            lng={selectedEquipment.lng}
            setCenter={setCenter}
            darkMode={darkMode}
          />
        }
      </GoogleMapReact>

      {selectedState && (
        <StatePanel
          data={selectedState}
          onClose={handleClosePanel}
          darkMode={darkMode}
        />
      )}
    </div>
  )
}

export default Map;
